import { useState } from "react";
import {
  useFormulationProducts,
  useFormulationVersions,
  useFormulationMaterials,
} from "@/hooks/use-formulations";
import { Loader2, ChevronRight, CheckCircle2, Clock, ScrollText } from "lucide-react";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";
import type { FmProduct, FmVersion, FmMaterial } from "@shared/types";

function ProductList({
  products,
  selected,
  onSelect,
}: {
  products: FmProduct[];
  selected: number | null;
  onSelect: (id: number) => void;
}) {
  return (
    <div className="space-y-1">
      <h3 className="px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Products ({products.length})
      </h3>
      {products.map((p) => (
        <button
          key={p.id}
          onClick={() => onSelect(p.id)}
          className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
            selected === p.id
              ? "bg-accent text-accent-foreground"
              : "hover:bg-muted/50"
          }`}
        >
          <div className="min-w-0">
            <p className="truncate font-medium">{p.name}</p>
            <p className="text-xs text-muted-foreground">
              {p.active_count} active / {p.recipe_count} total
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      ))}
    </div>
  );
}

function VersionList({
  versions,
  selected,
  onSelect,
}: {
  versions: FmVersion[];
  selected: number | null;
  onSelect: (id: number) => void;
}) {
  return (
    <div className="space-y-1">
      <h3 className="px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Versions ({versions.length})
      </h3>
      {versions.map((v) => {
        const label = v.notes || v.version || `#${v.id}`;
        const isActive = v.status === "ACTIVE";
        return (
          <button
            key={v.id}
            onClick={() => onSelect(v.id)}
            className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
              selected === v.id
                ? "bg-accent text-accent-foreground"
                : "hover:bg-muted/50"
            }`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate font-medium">{label}</p>
                {isActive ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-500">
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    <Clock className="h-3 w-3" /> Outdated
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {v.version || `v${v.id}`}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        );
      })}
    </div>
  );
}

function MaterialsTable({ materials }: { materials: FmMaterial[] }) {
  const total = materials.reduce((s, m) => s + Number(m.percentage), 0);
  return (
    <div>
      <h3 className="px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Bill of Materials
      </h3>
      <div className="rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">Material</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 text-right font-medium">%</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((m) => (
              <tr
                key={m.id}
                className="border-b border-border last:border-0"
              >
                <td className="px-3 py-2">{m.material_name}</td>
                <td className="px-3 py-2 text-muted-foreground">{m.material_type}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {Number(m.percentage).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-muted/30 font-medium">
              <td className="px-3 py-2" colSpan={2}>Total</td>
              <td className="px-3 py-2 text-right tabular-nums">
                {total.toFixed(1)}%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export function Formulations() {
  useSetPageHeader({
    title: "Formulations",
    subtitle: "Production formulation recipes and bill of materials",
    icon: ScrollText,
  });

  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

  const { data: products, isLoading: loadingProducts } =
    useFormulationProducts();
  const { data: versions, isLoading: loadingVersions } =
    useFormulationVersions(selectedProduct);
  const { data: materials, isLoading: loadingMaterials } =
    useFormulationMaterials(selectedVersion);

  const handleSelectProduct = (id: number) => {
    setSelectedProduct(id);
    setSelectedVersion(null);
  };

  if (loadingProducts) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Column 1: Products */}
        <div className="rounded-xl border border-border bg-card p-4">
          {products && products.length > 0 ? (
            <ProductList
              products={products}
              selected={selectedProduct}
              onSelect={handleSelectProduct}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              No formulations found
            </p>
          )}
        </div>

        {/* Column 2: Versions */}
        <div className="rounded-xl border border-border bg-card p-4">
          {selectedProduct === null ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Select a product to view formulation versions
            </p>
          ) : loadingVersions ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : versions && versions.length > 0 ? (
            <VersionList
              versions={versions}
              selected={selectedVersion}
              onSelect={setSelectedVersion}
            />
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No versions found
            </p>
          )}
        </div>

        {/* Column 3: Bill of Materials */}
        <div className="rounded-xl border border-border bg-card p-4">
          {selectedVersion === null ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Select a formulation version to view materials
            </p>
          ) : loadingMaterials ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : materials && materials.length > 0 ? (
            <MaterialsTable materials={materials} />
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No materials found
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
